import React, { useState, useEffect, useMemo, useRef } from "react";

const itemCd = (i) => i?.cdItem || i?.cditem || "";
const itemNm = (i) => i?.nmItem || i?.nmitem || "";
export const fmtItemEstoque = (i) => `${itemCd(i)} — ${itemNm(i)}`;

const norm = (s) => (s || "").toString().toLowerCase().replace(/\s+/g, " ").trim();

export function filtrarItensEstoque(itens, query, limit = 50) {
  const t = norm(query);
  if (!t) return (itens || []).slice(0, limit);
  return (itens || []).filter((i) => {
    const cd = norm(itemCd(i));
    const nm = norm(itemNm(i));
    return cd.includes(t) || nm.includes(t);
  }).slice(0, limit);
}

/** Combobox — busca por código ou descrição; só confirma item ao escolher na lista. */
export default function ItemEstoqueAutocomplete({
  itens = [],
  value = "",
  onChange,
  listId: _listId,
  placeholder = "Digite código ou descrição…",
  required = false,
  size = "sm",
  disabled = false,
}) {
  const wrapRef = useRef(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);

  const selected = useMemo(
    () => itens.find((x) => String(x.id) === String(value)),
    [itens, value]
  );

  useEffect(() => {
    if (typing) return;
    setText(selected ? fmtItemEstoque(selected) : "");
  }, [value, selected, typing]);

  const filtered = useMemo(
    () => (open ? filtrarItensEstoque(itens, text) : []),
    [itens, text, open]
  );

  const totalMatches = useMemo(() => {
    if (!open) return 0;
    const t = norm(text);
    if (!t) return itens.length;
    return (itens || []).filter((i) => {
      const cd = norm(itemCd(i));
      const nm = norm(itemNm(i));
      return cd.includes(t) || nm.includes(t);
    }).length;
  }, [itens, text, open]);

  const pick = (item) => {
    if (!item) return;
    setTyping(false);
    setOpen(false);
    setText(fmtItemEstoque(item));
    onChange(String(item.id));
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    setTyping(true);
    setOpen(true);
    if (selected && norm(fmtItemEstoque(selected)) !== norm(v)) {
      onChange("");
    }
  };

  const handleFocus = () => {
    setOpen(true);
    setTyping(true);
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      if (wrapRef.current?.contains(document.activeElement)) return;
      setOpen(false);
      setTyping(false);
      if (!text.trim()) {
        onChange("");
        setText("");
        return;
      }
      if (selected && norm(fmtItemEstoque(selected)) === norm(text)) return;
      const matches = filtrarItensEstoque(itens, text, 100);
      if (matches.length === 1) {
        pick(matches[0]);
        return;
      }
      const exact = itens.find((x) => norm(fmtItemEstoque(x)) === norm(text));
      if (exact) {
        pick(exact);
        return;
      }
      setText(selected ? fmtItemEstoque(selected) : "");
      if (!selected) onChange("");
    }, 120);
  };

  const cls = size === "sm" ? "form-control form-control-sm" : "form-control";

  return (
    <div className="position-relative" ref={wrapRef}>
      <input
        className={cls}
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open && filtered.length > 0}
      />
      {open && !disabled && filtered.length > 0 && (
        <ul
          className="dropdown-menu show shadow-sm"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1060,
            maxHeight: 220,
            overflowY: "auto",
            marginTop: 2,
          }}
        >
          {filtered.map((i) => (
            <li key={i.id}>
              <button
                type="button"
                className="dropdown-item small py-1 text-start"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(i)}
              >
                <span className="text-muted me-1">{itemCd(i)}</span>
                {itemNm(i)}
              </button>
            </li>
          ))}
          {totalMatches > filtered.length && (
            <li className="dropdown-item small text-muted disabled">
              +{totalMatches - filtered.length} resultado(s) — refine a busca
            </li>
          )}
        </ul>
      )}
      {open && !disabled && text.trim() && filtered.length === 0 && (
        <ul
          className="dropdown-menu show shadow-sm"
          style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1060, marginTop: 2 }}
        >
          <li className="dropdown-item small text-muted disabled">Nenhum item encontrado</li>
        </ul>
      )}
    </div>
  );
}
